import { EMPTY_DOC, textFromDoc, type ArticleDoc } from '@archetelos/editor';

// Taxonomy is canonical in @archetelos/lib; re-exported here (DISPLAY alias) so
// existing composer imports keep resolving.
export { TRADITIONS, TOPICS, TAXONOMY_DISPLAY as DISPLAY } from '@archetelos/lib';
export { EMPTY_DOC } from '@archetelos/editor';
export type { ArticleDoc } from '@archetelos/editor';

export type GatePolicy = 'free_14_days' | 'subscribers_only';
export type AccessTier = 'always_free' | 'metered' | 'members_only';

export interface ComposerState {
  title: string;
  excerpt: string;
  slug: string;
  bodyJson: ArticleDoc;
  // Canonical cover reference (Spec 41). coverImageUrl/coverImageAlt are the
  // display cache kept in sync with the chosen asset — shown in the composer and
  // snapshotted into versions, but the DB cache columns are written by trigger,
  // never by the client.
  coverAssetId: string | null;
  coverImageUrl: string;
  coverImageAlt: string;
  traditionTags: string[];
  topicTags: string[];
  gatePolicy: GatePolicy;
  accessTier: AccessTier;
}

export interface ComposerValidationError {
  field: string;
  message: string;
}

export function validateForSubmit(s: ComposerState): ComposerValidationError[] {
  const errs: ComposerValidationError[] = [];
  if (!s.title.trim()) errs.push({ field: 'title', message: 'A title is required.' });
  if (!s.excerpt.trim()) errs.push({ field: 'excerpt', message: 'An excerpt is required.' });
  if (textFromDoc(s.bodyJson).trim().length < 500)
    errs.push({ field: 'bodyJson', message: 'The body must be at least 500 characters.' });
  // A cover is now a chosen asset; alt lives on the asset (always non-null), so
  // there's no separate alt requirement to enforce here.
  if (!s.coverAssetId) errs.push({ field: 'coverAssetId', message: 'A cover image is required.' });
  if (s.traditionTags.length === 0)
    errs.push({ field: 'traditionTags', message: 'Select at least one tradition.' });
  if (s.topicTags.length === 0)
    errs.push({ field: 'topicTags', message: 'Select at least one topic.' });
  return errs;
}

/** A blank composer state (new article). */
export function emptyComposerState(): ComposerState {
  return {
    title: '',
    excerpt: '',
    slug: '',
    bodyJson: EMPTY_DOC,
    coverAssetId: null,
    coverImageUrl: '',
    coverImageAlt: '',
    traditionTags: [],
    topicTags: [],
    gatePolicy: 'free_14_days',
    accessTier: 'metered',
  };
}

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}
